import { loginApi } from "@/api/services/main/auth";
import { IconGoogle } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { Checkbox } from "@/components/elements/Checkbox";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Linkable } from "@/components/elements/Linkable";
import { EMAIL_KEY } from "@/constants/common";
import { LOGIN_CACHED_EMAIL } from "@/features/auth/constant/login";
import { usePasskeyLogin } from "@/features/auth/hooks/usePasskeyLogin";
import { loginCredentialsSchema } from "@/features/auth/schema/loginCredentialsSchema";
import { TLoginCredentialsForm } from "@/features/auth/types/loginCredentialsForm";
import { buildCognitoGoogleLoginUrl } from "@/features/auth/utils/buildCognitoGoogleLoginUrl";
import { encodeCognitoAuthState } from "@/features/auth/utils/cognitoAuthState";
import { isPasskeySupported } from "@/features/auth/utils/passkey";
import { useCacheLocal } from "@/hooks/useCacheLocal";
import { AppRoutes } from "@/routes/config";
import { state, text } from "@/theme/colors";
import { setErrorForm, showError } from "@/utils/error";
import { createUrlWithParams, getQueryFromURL } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import KeyIcon from "@mui/icons-material/Key";
import LoginIcon from "@mui/icons-material/Login";
import { Box, Divider, Paper, Stack, Typography, styled } from "@mui/material";
import { FC, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

type TLoginCredentialsFormProps = {
  externalId: string;
};

export const LoginCredentialsForm: FC<TLoginCredentialsFormProps> = ({ externalId }) => {
  const cachedEmailStore = useCacheLocal(LOGIN_CACHED_EMAIL);
  const isAlreadyCache = cachedEmailStore.isExist();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [remember, setRemember] = useState(isAlreadyCache);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const { isPasskeyLoading, handlePasskeyLogin } = usePasskeyLogin({ autoStart: true });
  const passkeyEnabled = useMemo(() => isPasskeySupported(), []);

  const cachedData = useMemo(() => {
    if (!isAlreadyCache) return {};
    const cachedEmailMap = cachedEmailStore.getJSON();
    if (typeof cachedEmailMap === "object") return cachedEmailMap as Record<string, string>;
    return {};
  }, [cachedEmailStore, isAlreadyCache]);

  const email = useMemo(() => {
    if (searchParams.has(EMAIL_KEY)) return searchParams.get(EMAIL_KEY) as string;
    if (cachedData[externalId]) return cachedData[externalId];
    return "";
  }, [searchParams, cachedData, externalId]);

  const defaultValues = useMemo(() => {
    return {
      externalId,
      email,
    };
  }, [externalId, email]);

  const methods = useForm<TLoginCredentialsForm>({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(loginCredentialsSchema),
  });
  const { control } = methods;

  const handleToggleRemember = (_: unknown, checked: boolean) => {
    setRemember(checked);
  };

  const handleGoogleLogin = () => {
    setIsGoogleLoading(true);
    // Carry everything the callback needs through the OAuth `state` param instead of
    // web storage; Cognito echoes it back to the AuthCallbackCognito page.
    const query = getQueryFromURL();
    const state = encodeCognitoAuthState({
      ...query,
      externalId,
      email,
    });
    globalThis.location.href = buildCognitoGoogleLoginUrl(state);
  };

  const onSubmit = async (data: TLoginCredentialsForm) => {
    try {
      const res = await loginApi({
        externalId,
        email: data.email,
        password: data.password,
      });
      const { pendingAuthToken, email: resEmail, externalId: resExternalId } = res.data;

      if (remember) {
        cachedEmailStore.setJSON({ ...cachedData, [externalId]: email });
      } else {
        delete cachedData[externalId];
        cachedEmailStore.setJSON(cachedData);
      }

      const query = getQueryFromURL();
      navigate(AppRoutes.loginVerify, {
        state: {
          ...query,
          pendingAuthToken,
          email: resEmail,
          externalId: resExternalId,
        },
      });
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const handleOpenLoginForm = () => {
    const query = getQueryFromURL();
    navigate(createUrlWithParams(AppRoutes.login, { ...query, externalId }));
  };

  return (
    <Container>
      <Form methods={methods} onSubmit={onSubmit} enableBlocker={false} schema={loginCredentialsSchema}>
        {passkeyEnabled && (
          <Stack width={400}>
            <Button size="lg" variant="primary" fullWidth onClick={handlePasskeyLogin} isLoading={isPasskeyLoading}>
              <Stack direction="row" gap={1} alignItems="center" justifyContent="center">
                <KeyIcon sx={{ fontSize: 20 }} />
                <Typography variant="sub16Semi">パスキーでログイン</Typography>
              </Stack>
            </Button>
            <Box py="20px">
              <Divider>
                <Typography variant="body14" color={text.secondary}>
                  またはメール・Googleでログイン
                </Typography>
              </Divider>
            </Box>
          </Stack>
        )}
        <Stack gap="28px" width={400}>
          <Stack gap={0.5}>
            <FormField label="組織ID">
              <Input size="lg" value={externalId} readonly />
            </FormField>
            <Typography
              variant="cap12"
              color={state.link_1}
              sx={{ textDecoration: "underline", whiteSpace: "nowrap", cursor: "pointer" }}
              onClick={handleOpenLoginForm}
            >
              組織IDが異なる場合はこちらから変更できます
            </Typography>
          </Stack>

          <Stack flexDirection="column" gap={2}>
            <Stack flexDirection="column" gap="24px">
              <FormField control={control} name="email" label="メールアドレス" required>
                <Input size="lg" placeholder="入力してください" />
              </FormField>
              <Stack flexDirection="column" gap="12px">
                <FormField
                  control={control}
                  name="password"
                  label="パスワード"
                  required
                  extendedHelper={
                    <Linkable href={`${AppRoutes.recover}?externalId=${encodeURIComponent(externalId)}`} ml="auto" pb="8px">
                      <Typography variant="cap12" color={state.link_1} sx={{ textDecoration: "underline", whiteSpace: "nowrap", cursor: "pointer" }}>
                        パスワードを忘れた方はこちら
                      </Typography>
                    </Linkable>
                  }
                >
                  <Input size="lg" type="password" />
                </FormField>

                <Checkbox checked={remember} onChange={handleToggleRemember} label="メールアドレスをWebブラウザに保存する" alignStart={false} />
              </Stack>
            </Stack>

            <Button type="submit" size="lg" variant="outline" fullWidth>
              <Stack direction="row" gap={1} alignItems="center" justifyContent="center">
                <LoginIcon fontSize="small" />
                <Typography variant="body14Semi">ログイン</Typography>
              </Stack>
            </Button>
          </Stack>
        </Stack>
        <Box my="20px">
          <Divider />
        </Box>
        <Button size="lg" variant="outline" fullWidth onClick={handleGoogleLogin} isLoading={isGoogleLoading}>
          <Stack direction="row" gap={1} alignItems="center" justifyContent="center">
            <IconGoogle fontSize={20} style={{ filter: isGoogleLoading ? "grayscale(100%)" : "none" }} />
            <Typography variant="body14Semi">Googleでログイン</Typography>
          </Stack>
        </Button>
      </Form>
    </Container>
  );
};

const Container = styled(Paper)`
  border-radius: 12px;
  padding: 32px;
  box-shadow:
    0 4px 4px -1px rgba(12, 12, 13, 0.05),
    0 4px 4px -1px rgba(12, 12, 13, 0.1) !important;
`;
